import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { UserContext } from '../UserContext';
import { SubscriptionContext } from '../../reducers/SubscriptionContext';
import UpgradeModal from '../UpgradeModels/UpgradeModal';
import { Auth } from 'aws-amplify';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import '../Card/card-style.css';
import { css } from '@emotion/css';

const styles = {
  container: css`
    min-height: 100vh;
    background: linear-gradient(135deg, #f8fafc 0%, #fff7ed 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
  `,
  card: css`
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.5);
    border-radius: 24px;
    padding: 3rem;
    box-shadow: 0 25px 50px -12px rgba(241, 102, 2, 0.15);
    width: 100%;
    max-width: 600px;
  `,
  header: css`
    text-align: center;
    margin-bottom: 2.5rem;
  `,
  title: css`
    font-size: 2.5rem;
    font-weight: 800;
    color: #c2410c;
    margin-bottom: 0.5rem;
    line-height: 1.2;
  `,
  subtitle: css`
    font-size: 1.1rem;
    color: #d94f00;
    font-weight: 500;
  `,
  formGroup: css`
    margin-bottom: 1.5rem;
  `,
  label: css`
    font-size: 0.85rem;
    font-weight: 700;
    color: #f16602;
    margin-bottom: 8px;
    display: block;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  `,
  select: css`
    width: 100%;
    border: 1.5px solid rgba(241, 102, 2, 0.4);
    border-radius: 12px;
    padding: 12px 16px;
    font-weight: 600;
    font-size: 1rem;
    color: #d94f00;
    background: #fff;
    cursor: pointer;
    outline: none;
    transition: all 0.2s ease;
    &:focus {
      border-color: #f16602;
      box-shadow: 0 0 0 4px rgba(241, 102, 2, 0.1);
    }
  `,
  sliderWrap: css`
    display: flex;
    align-items: center;
    gap: 1rem;
  `,
  slider: css`
    flex: 1;
    -webkit-appearance: none;
    width: 100%;
    height: 8px;
    border-radius: 4px;
    background: #ffedd5;
    outline: none;
    
    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background: #f16602;
      cursor: pointer;
      box-shadow: 0 2px 6px rgba(241, 102, 2, 0.4);
      transition: transform 0.1s;
    }

    &::-webkit-slider-thumb:hover {
      transform: scale(1.15);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `,
  sliderValue: css`
    font-size: 1.1rem;
    font-weight: 700;
    color: #c2410c;
    min-width: 3rem;
    text-align: right;
  `,
  button: css`
    width: 100%;
    background: linear-gradient(135deg, #f16602 0%, #d94f00 100%);
    color: white;
    border: none;
    border-radius: 12px;
    padding: 16px;
    font-size: 1.1rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-top: 2rem;
    box-shadow: 0 10px 20px -5px rgba(241, 102, 2, 0.3);
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 15px 25px -5px rgba(241, 102, 2, 0.4);
    }
  `
};

export default function DailyReviewScreen() {
  const history = useHistory();
  const { user } = useContext(UserContext);
  const { subscriptionStatus } = useContext(SubscriptionContext);
  
  const [gradeUser, setGradeUser] = useState(5);
  const [reviewType, setReviewType] = useState('spaced_repetition');
  const [customDays, setCustomDays] = useState(7);
  const [reviewStartLimit, setReviewStartLimit] = useState(1);
  const [reviewEndLimit, setReviewEndLimit] = useState(15);
  const [reviewTimeLimit, setReviewTimeLimit] = useState(60);
  const [maxAvailableCount, setMaxAvailableCount] = useState(null);
  const [randomize, setRandomize] = useState(true);
  const [countLoading, setCountLoading] = useState(false);
  const [countError, setCountError] = useState(false);

  const timeOptions = [0, 5, 10, 15, 30, 45, 60, 90, 120, 180, 300];
  const limitOptions = [5, 10, 15, 20, 25, 30, 50];

  useEffect(() => {
    if (user && Object.keys(user).length > 0) {
      setGradeUser(user.grade || 5);
    }
  }, [user]);

  // Fetch max available questions count when review strategy changes
  useEffect(() => {
    let isActive = true;
    
    const fetchMaxQuestions = async () => {
      if (isActive) {
        setCountLoading(true);
        setCountError(false);
      }
      try {
        const currentUser = await Auth.currentAuthenticatedUser();
        const session = await Auth.currentSession();
        const jwt = session.getAccessToken().getJwtToken();
        const username = currentUser.username.includes("Google_") ? currentUser.attributes['email'] : currentUser.username;

        const response = await fetch('https://api.quizwizard.net/quiz/get_review_queue', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': jwt
          },
          body: JSON.stringify({
            username,
            level: gradeUser,
            category: 'SpellBowl',
            reviewType,
            reviewDays: reviewType === 'targeted' ? (customDays || 7) : undefined,
            days: reviewType === 'targeted' ? (customDays || 7) : undefined,
            review_days: reviewType === 'targeted' ? (customDays || 7) : undefined,
            countOnly: true
          })
        });

        if (!response.ok) {
          throw new Error(`get_review_queue responded with ${response.status}`);
        }

        const data = await response.json();
        if (isActive) {
          const count = typeof data.count === 'number' ? data.count : 0;
          setMaxAvailableCount(count);
          if (count > 0) {
            setReviewEndLimit(count);
            setReviewStartLimit(1);
          } else {
            setReviewStartLimit(0);
            setReviewEndLimit(0);
          }
        }
      } catch (error) {
        console.error('Error fetching max available questions:', error);
        if (isActive) {
          setCountError(true);
        }
      } finally {
        if (isActive) {
          setCountLoading(false);
        }
      }
    };

    if (gradeUser) {
      fetchMaxQuestions();
    }

    return () => {
      isActive = false;
    };
  }, [reviewType, gradeUser, customDays]);

  // Dynamically build limit options based on max available count
  let dynamicLimitOptions = [5, 10, 15, 20, 25, 30, 50];
  if (maxAvailableCount !== null) {
    dynamicLimitOptions = dynamicLimitOptions.filter(l => l < maxAvailableCount);
    if (maxAvailableCount > 0 && !dynamicLimitOptions.includes(maxAvailableCount)) {
      dynamicLimitOptions.push(maxAvailableCount);
    }
  }

  // For gating
  const isPremium = subscriptionStatus === 'active';

  const handleSliderChange = useCallback((value) => {
    setReviewStartLimit(value[0]);
    setReviewEndLimit(value[1]);
  }, []);

  const handleStartReview = () => {
    history.push({
      pathname: '/practicequiz',
      state: { 
        category: 'SpellBowl', 
        specificcategory: 'SpellBowl',
        isDailyReview: true, 
        level: gradeUser, 
        time: reviewTimeLimit,
        reviewType: reviewType,
        reviewDays: reviewType === 'targeted' ? (customDays || 7) : undefined,
        reviewQuestionLimit: (reviewEndLimit || 15) - (reviewStartLimit || 1) + 1,
        reviewStartLimit: reviewStartLimit || 1,
        eventtype: 'Practice',
        name: 'Daily SpellBowl Review',
        eventdisplay: '',
        randomize: randomize
      }
    });
  };

  return (
    <div className={styles.container}>
      {!isPremium && (
        <UpgradeModal show={true} onClose={() => history.push('/')} />
      )}

      <div className={styles.card}>
        <div className={styles.header}>
          <h1 className={styles.title}>Daily SpellBowl Review</h1>
          <p className={styles.subtitle}>Supercharge your memory with personalized practice</p>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Review Strategy</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', padding: '12px', borderRadius: '12px', border: reviewType === 'spaced_repetition' ? '2px solid #f16602' : '2px solid transparent', background: reviewType === 'spaced_repetition' ? '#fff7ed' : '#f8fafc', transition: 'all 0.2s' }}>
              <input type="radio" name="strategy" value="spaced_repetition" checked={reviewType === 'spaced_repetition'} onChange={(e) => setReviewType(e.target.value)} style={{ width: '18px', height: '18px', accentColor: '#f16602' }} />
              <div>
                <div style={{ fontWeight: '700', color: '#c2410c' }}>Smart Review</div>
                <div style={{ fontSize: '0.85rem', color: '#d94f00', marginTop: '2px' }}>Review questions that you are most likely to forget based on your past performance.</div>
              </div>
            </label>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', padding: '12px', borderRadius: '12px', border: reviewType === 'targeted' ? '2px solid #f16602' : '2px solid transparent', background: reviewType === 'targeted' ? '#fff7ed' : '#f8fafc', transition: 'all 0.2s' }}>
              <input type="radio" name="strategy" value="targeted" checked={reviewType === 'targeted'} onChange={(e) => setReviewType(e.target.value)} style={{ width: '18px', height: '18px', accentColor: '#f16602', marginTop: '4px' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '700', color: '#c2410c' }}>Targeted Practice</div>
                <div style={{ fontSize: '0.85rem', color: '#d94f00', marginTop: '2px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                  <span>Review all words missed in the past</span>
                  <input 
                    type="number" 
                    min="1" 
                    max="365"
                    value={customDays} 
                    onChange={(e) => {
                      if (e.target.value === '') {
                        setCustomDays('');
                        return;
                      }
                      let val = parseInt(e.target.value);
                      if (!isNaN(val) && val > 0) setCustomDays(val);
                    }}
                    onClick={(e) => setReviewType('targeted')}
                    disabled={reviewType !== 'targeted'}
                    style={{ width: '50px', padding: '4px 8px', border: '1.5px solid #f16602', borderRadius: '6px', textAlign: 'center', outline: 'none', background: reviewType === 'targeted' ? '#fff' : '#e2e8f0' }} 
                  />
                  <span>days.</span>
                </div>
              </div>
            </label>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>
            Select Questions to Review {maxAvailableCount !== null && <span style={{color: '#fdba74', fontWeight: 500}}>(Max {maxAvailableCount})</span>}
            {countLoading && <span style={{color: '#fdba74', fontWeight: 500, marginLeft: '6px'}}>(updating...)</span>}
          </label>
          {reviewType === 'targeted' && !countError && (
            <div style={{ fontSize: '0.8rem', color: '#c2410c', marginTop: '-4px', marginBottom: '8px' }}>
              Based on words missed in the last <strong>{customDays || 0}</strong> day{(customDays || 0) === 1 ? '' : 's'}
            </div>
          )}
          {countError && (
            <div style={{ fontSize: '0.8rem', color: '#dc2626', marginTop: '-4px', marginBottom: '8px', fontWeight: 600 }}>
              Couldn't load the count for this selection. The number above may be out of date — try changing the value again.
            </div>
          )}
          <div className="daily-review-slider-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '16px', opacity: maxAvailableCount === 0 ? 0.5 : 1, pointerEvents: maxAvailableCount === 0 ? 'none' : 'auto' }}>
            <span style={{fontWeight: '600', color: '#c2410c'}}>Range:</span>
            <input
              type="number"
              min={1}
              max={maxAvailableCount === 0 ? 0 : reviewEndLimit - 1}
              value={maxAvailableCount === 0 ? 0 : (reviewStartLimit === '' ? '' : reviewStartLimit)}
              onChange={(e) => {
                if (e.target.value === '') {
                  setReviewStartLimit('');
                  return;
                }
                let val = parseInt(e.target.value);
                if (!isNaN(val) && val >= 1 && val < (reviewEndLimit || Infinity)) setReviewStartLimit(val);
              }}
              style={{ width: '60px', padding: '4px 8px', border: '2px solid #fed7aa', borderRadius: '6px', textAlign: 'center', outline: 'none' }}
            />
            <Slider
              range
              trackStyle={{ backgroundColor: '#f16602', height: 6 }}
              handleStyle={[{ borderColor: '#f16602', height: 16, width: 16, marginTop: -5, backgroundColor: '#ffffff' }, { borderColor: '#f16602', height: 16, width: 16, marginTop: -5, backgroundColor: '#ffffff' }]}
              railStyle={{ backgroundColor: 'rgba(241, 102, 2, 0.15)', height: 6 }}
              min={maxAvailableCount === 0 ? 0 : 1}
              max={maxAvailableCount !== null ? maxAvailableCount : 50}
              value={[maxAvailableCount === 0 ? 0 : (reviewStartLimit || 1), maxAvailableCount === 0 ? 0 : (reviewEndLimit || 15)]}
              onChange={handleSliderChange}
              style={{ flex: 1, minWidth: 60 }}
            />
            <input
              type="number"
              min={maxAvailableCount === 0 ? 0 : reviewStartLimit + 1}
              max={maxAvailableCount !== null ? maxAvailableCount : 50}
              value={maxAvailableCount === 0 ? 0 : (reviewEndLimit === '' ? '' : reviewEndLimit)}
              onChange={(e) => {
                if (e.target.value === '') {
                  setReviewEndLimit('');
                  return;
                }
                let val = parseInt(e.target.value);
                let maxLim = maxAvailableCount !== null ? maxAvailableCount : 50;
                if (!isNaN(val) && val > (reviewStartLimit || 0) && val <= maxLim) setReviewEndLimit(val);
              }}
              style={{ width: '60px', padding: '4px 8px', border: '2px solid #fed7aa', borderRadius: '6px', textAlign: 'center', outline: 'none' }}
            />
          </div>
          <div style={{ textAlign: 'center', marginTop: '16px', fontSize: '0.9rem', color: '#c2410c', fontWeight: '600' }}>
            {maxAvailableCount === 0 ? '0 words' : `Selected: Questions ${reviewStartLimit} to ${reviewEndLimit} (${reviewEndLimit - reviewStartLimit + 1} total)`}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginTop: '20px', fontWeight: '700', fontSize: '0.85rem', color: '#c2410c', textTransform: 'uppercase', letterSpacing: '0.05em', opacity: maxAvailableCount === 0 ? 0.5 : 1, pointerEvents: maxAvailableCount === 0 ? 'none' : 'auto' }}>
            <span>Shuffle Questions</span>
            <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px' }}>
              <input type="checkbox" checked={randomize} onChange={(e) => setRandomize(e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} />
              <span style={{ position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: randomize ? '#f16602' : 'rgba(241, 102, 2, 0.15)', transition: '.4s', border: `1px solid ${randomize ? '#f16602' : 'rgba(241, 102, 2, 0.18)'}`, borderRadius: '34px' }}></span>
              <span style={{ position: 'absolute', height: '16px', width: '16px', left: randomize ? '23px' : '3px', bottom: '3px', backgroundColor: 'white', transition: '.4s', borderRadius: '50%', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}></span>
            </label>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Time per question</label>
          <select
            className={styles.select}
            value={reviewTimeLimit}
            onChange={(e) => setReviewTimeLimit(parseInt(e.target.value))}
          >
            {timeOptions.map((t) => {
              let displayLabel = '';
              if (t === 0) displayLabel = 'No time limit';
              else if (t >= 60) {
                const m = t / 60;
                displayLabel = `${m === Math.floor(m) ? m : m.toFixed(1)} min`;
              } else displayLabel = `${t} sec`;
              
              return (
                <option key={t} value={t}>
                  {displayLabel}
                </option>
              );
            })}
          </select>
        </div>

        <button 
          className={styles.button} 
          onClick={handleStartReview}
          disabled={!isPremium || maxAvailableCount === 0}
          style={{ opacity: (!isPremium || maxAvailableCount === 0) ? 0.6 : 1, cursor: (!isPremium || maxAvailableCount === 0) ? 'not-allowed' : 'pointer' }}
        >
          {maxAvailableCount === 0 ? 'No words due for review!' : 'Start Review Session'}
        </button>
      </div>
    </div>
  );
}
